export type EventCallback<T extends Event = Event> = (event: T) => void;

interface DelegatedEvent<T extends Event = Event> {
  type: string;
  selector: string;
  handler: EventCallback<T>;
  wrapped: EventListener;
}

const FOCUS_MAP: Record<string, string> = {
  focus: 'focusin',
  blur: 'focusout',
};

export class Delegate {
  private _root: Element;
  private _events: DelegatedEvent[] = [];
  private _rootListeners: Map<string, EventListener> = new Map();

  constructor(rootElement: Element) {
    this._root = rootElement;
  }

  on<T extends Event>(type: string, selector: string, handler: EventCallback<T>): void {
    const actualType = FOCUS_MAP[type] ?? type;

    const wrapped: EventListener = (event: Event) => {
      const origin = event.target;
      if (!(origin instanceof Element)) return;

      const matched = origin.closest(selector);
      if (!matched || !this._root.contains(matched)) return;
      Object.defineProperty(event, 'originalTarget', { value: origin, configurable: true });
      Object.defineProperty(event, 'target', { value: matched, configurable: true });
      handler(event as T);
    };

    this._events.push({ type: actualType, selector, handler: handler as EventCallback, wrapped });

    if (!this._rootListeners.has(actualType)) {
      this._rootListeners.set(actualType, (e: Event) => {
        const originalTarget = e.target;
        for (const entry of this._events) {
          if (entry.type === actualType) {
           
            Object.defineProperty(e, 'target', { value: originalTarget, configurable: true });
            entry.wrapped(e);
          }
        }
      });
      this._root.addEventListener(actualType, this._rootListeners.get(actualType)!);
    }
  }

  off<T extends Event>(type: string, selector: string, handler: EventCallback<T>): void {
    const actualType = FOCUS_MAP[type] ?? type;

    this._events = this._events.filter((entry) => {
      if (entry.type === actualType && entry.selector === selector && entry.handler === (handler as EventCallback)) {
        return false;
      }
      return true;
    });

    this._cleanupRootListener(actualType);
  }

  offAll(type?: string, selector?: string): void {
    if (!type) {
      const types = new Set(this._events.map((e) => e.type));
      this._events = [];
      for (const t of types) {
        this._cleanupRootListener(t);
      }
      return;
    }

    const actualType = FOCUS_MAP[type] ?? type;

    this._events = this._events.filter((entry) => {
      if (entry.type !== actualType) return true;
      if (selector && entry.selector !== selector) return true;
      return false;
    });

    this._cleanupRootListener(actualType);
  }

  destroy(): void {
    for (const [type, listener] of this._rootListeners) {
      this._root.removeEventListener(type, listener);
    }
    this._rootListeners.clear();
    this._events = [];
  }

  hasListeners(type: string, selector?: string): boolean {
    const actualType = FOCUS_MAP[type] ?? type;

    return this._events.some((entry) => {
      if (entry.type !== actualType) return false;
      if (selector && entry.selector !== selector) return false;
      return true;
    });
  }

  private _cleanupRootListener(actualType: string): void {
    const hasRemaining = this._events.some((e) => e.type === actualType);
    if (!hasRemaining) {
      const listener = this._rootListeners.get(actualType);
      if (listener) {
        this._root.removeEventListener(actualType, listener);
        this._rootListeners.delete(actualType);
      }
    }
  }
}
